import React from 'react';
import type { Message } from '@/types/database';
import { MoreVertical, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessageListProps {
  messages: Message[];
  currentUserId?: string;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onDeleteMessage: (messageId: string) => void;
}

export function MessageList({ messages, currentUserId, messagesEndRef, onDeleteMessage }: MessageListProps) {
  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50 dark:bg-dark-elevated">
      {messages.map((message, index) => {
        const isCurrentUser = message.sender_id === currentUserId;
        const showAvatar = index === 0 || 
          messages[index - 1].sender_id !== message.sender_id;

        return (
          <div
            key={message.id}
            className={cn(
              "flex gap-3",
              isCurrentUser ? "flex-row-reverse" : "flex-row"
            )}
          >
            {showAvatar && (
              <img
                src={message.sender?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${message.sender?.full_name}`}
                alt={message.sender?.full_name}
                className="w-8 h-8 rounded-full ring-2 ring-royal-purple/20 flex-shrink-0"
              />
            )}
            <div
              className={cn(
                "group max-w-[70%] space-y-1",
                !showAvatar && (isCurrentUser ? "mr-11" : "ml-11")
              )}
            >
              {showAvatar && (
                <p className={cn(
                  "text-sm font-medium text-gray-900 dark:text-gray-100",
                  isCurrentUser ? "text-right" : "text-left"
                )}>
                  {message.sender?.full_name}
                </p>
              )}
              
              <div className="relative">
                <div className={cn(
                  "rounded-2xl p-3 group-hover:shadow-md transition-shadow",
                  isCurrentUser
                    ? "bg-royal-purple text-white dark:bg-royal-purple/90 dark:text-white rounded-tr-none"
                    : "bg-white dark:bg-dark-paper text-gray-900 dark:text-gray-100 rounded-tl-none"
                )}>
                  {message.content && (
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  )}
                  
                  {message.media_url && (
                    message.media_type === 'video' ? (
                      <video
                        src={message.media_url}
                        controls
                        className="rounded-lg mt-2 max-w-full"
                      />
                    ) : (
                      <img
                        src={message.media_url}
                        alt="Message attachment"
                        className="rounded-lg mt-2 max-w-full"
                      />
                    )
                  )}
                </div>

                {isCurrentUser && (
                  <div className="absolute top-0 right-0 -mr-8 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onDeleteMessage(message.id)}
                      className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                      title="Delete message"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <p className={cn(
                "text-xs text-gray-500 dark:text-gray-400",
                isCurrentUser ? "text-right" : "text-left"
              )}>
                {new Date(message.created_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}